import Header from '../components/Header';
import Footer from '../components/Footer';

const sections = [
	{
		id: 'effective-date',
		title: 'Effective Date',
		content: (
			<p className="text-slate-600 leading-relaxed">Effective Date: June 5, 2024</p>
		),
	},
	{
		id: 'your-right',
		title: 'Your Right to Data Deletion',
		content: (
			<p className="text-slate-600 leading-relaxed">
				You may request deletion of your personal data under GDPR, applicable Indian privacy regulations, and other applicable laws. Viance processes all deletion requests transparently and promptly.
			</p>
		),
	},
	{
		id: 'direct-request',
		title: '1. Direct Data Deletion Request',
		content: (
			<div className="space-y-3">
				<p className="text-slate-600 leading-relaxed">
					Email us at{' '}
					<a className="text-cyan-600 hover:underline" href="mailto:admin@viance.co.in">
						admin@viance.co.in
					</a>{' '}
					with the subject line <span className="font-semibold">"Data Deletion Request"</span>. Please include:
				</p>
				<ul className="list-disc pl-6 space-y-1 text-slate-600">
					<li>Platform / App name</li>
					<li>Registered email address / username / user ID</li>
					<li>Approximate registration or first-use date</li>
					<li>Access channel (Web, App Store, Play Store, etc.)</li>
					<li>Specific data categories of concern (optional)</li>
				</ul>
			</div>
		),
	},
	{
		id: 'timeline',
		title: '2. Data Deletion Timeline &amp; Process',
		content: (
			<div className="space-y-3">
				<ol className="list-decimal pl-6 space-y-2 text-slate-600">
					<li>Receipt acknowledgment (≤ 48 hours)</li>
					<li>Identity verification (2–5 business days)</li>
					<li>Data purge from active systems (within 30 days)</li>
					<li>Confirmation email once complete</li>
				</ol>
			</div>
		),
	},
	{
		id: 'what-deleted',
		title: '3. What Will Be Deleted',
		content: (
			<ul className="list-disc pl-6 space-y-1 text-slate-600">
				<li>Personal identifiers (name, email, phone number)</li>
				<li>Credentials &amp; access tokens</li>
				<li>Usage logs &amp; analytics (non‑aggregated)</li>
				<li>Preferences &amp; settings</li>
				<li>Support / conversation history</li>
				<li>Uploaded or generated content (unless legal retention applies)</li>
			</ul>
		),
	},
	{
		id: 'retention-exceptions',
		title: '4. Retention Exceptions',
		content: (
			<p className="text-slate-600 leading-relaxed">
				Certain data may be retained where required by law or legitimate business purpose:
			</p>
		),
	},
	{
		id: 'retention-list',
		title: '',
		content: (
			<ul className="list-disc pl-6 space-y-1 text-slate-600 -mt-6">
				<li>Legal, tax, or audit obligations</li>
				<li>Security &amp; fraud prevention logs (anonymised)</li>
				<li>Aggregated analytics (non-identifiable)</li>
				<li>Backup media (removed on scheduled purge cycles)</li>
			</ul>
		),
	},
	{
		id: 'third-party',
		title: '5. Third-Party Services',
		content: (
			<p className="text-slate-600 leading-relaxed">
				If you authenticated via Google, Apple, Microsoft, or other third-party identity providers, please also revoke permissions in the respective account settings for those platforms.
			</p>
		),
	},
	{
		id: 'verification',
		title: '6. Verification &amp; Security',
		content: (
			<p className="text-slate-600 leading-relaxed">
				We may request additional proof of identity or account ownership to prevent unauthorised deletion of another person's data.
			</p>
		),
	},
	{
		id: 'post-deletion',
		title: '7. Post-Deletion Access',
		content: (
			<p className="text-slate-600 leading-relaxed">
				Deletion is irreversible. Once processed, you will lose access to all data and services associated with the deleted account.
			</p>
		),
	},
	{
		id: 'alternatives',
		title: '8. Alternative Options',
		content: (
			<ul className="list-disc pl-6 space-y-1 text-slate-600">
				<li>Account deactivation (without full deletion)</li>
				<li>Data export / portability</li>
				<li>Selective deletion of specific data categories</li>
				<li>Adjusting privacy or communication preferences</li>
			</ul>
		),
	},
	{
		id: 'contact',
		title: '9. Contact Information',
		content: (
			<div className="space-y-2 text-slate-600">
				<p className="font-semibold text-slate-800">VIANCE HEALTHCARE SYSTEMS PRIVATE LIMITED</p>
				<p>Sulochana Apartments, Income Tax Lane, Pune – 411004, Maharashtra, India</p>
				<p>
					Email:{' '}
					<a className="text-cyan-600 hover:underline" href="mailto:admin@viance.co.in">
						admin@viance.co.in
					</a>
				</p>
				<p>
					Phone:{' '}
					<a className="text-cyan-600 hover:underline" href="tel:+919823272353">
						+91 9823272353
					</a>
				</p>
			</div>
		),
	},
	{
		id: 'updates',
		title: '10. Updates to These Instructions',
		content: (
			<p className="text-slate-600 leading-relaxed">
				We may revise these instructions to reflect operational, legal, or regulatory changes. Continued use of our services after updates signifies acknowledgment of the revised instructions.
			</p>
		),
	},
];

function DataDeletion() {
	return (
		<div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100 to-slate-100 text-slate-900">
			<Header />
			<main className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
				{/* Page header */}
				<div className="mb-10">
					<span className="inline-block rounded-full bg-cyan-100 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-cyan-700">
						Legal
					</span>
					<h1 className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900">
						Data Deletion Instructions
					</h1>
					<p className="mt-3 max-w-2xl text-slate-600">
						How to request erasure of data associated with your Viance account and integrations.
					</p>
					<p className="mt-2 text-sm text-slate-400">Last updated: 2026</p>
				</div>

				{/* Quick action card */}
				<div className="mb-10 rounded-2xl bg-cyan-50 p-6 ring-1 ring-cyan-200">
					<h2 className="text-lg font-bold text-cyan-900 mb-2">Request Deletion Quickly</h2>
					<p className="text-cyan-800 text-sm mb-4">
						Send an email with subject <span className="font-semibold">"Data Deletion Request"</span> to:
					</p>
					<a
						href="mailto:admin@viance.co.in?subject=Data%20Deletion%20Request"
						className="inline-block rounded-lg bg-cyan-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-cyan-700 transition-colors"
					>
						admin@viance.co.in
					</a>
				</div>

				{/* Table of contents */}
				<nav className="mb-10 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
					<h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-cyan-700">Contents</h2>
					<ol className="space-y-1 text-sm text-slate-600">
						{sections
							.filter((s) => s.title)
							.map((s) => (
								<li key={s.id}>
									<a href={`#${s.id}`} className="hover:text-cyan-600 transition-colors"
										dangerouslySetInnerHTML={{ __html: s.title }}
									/>
								</li>
							))}
					</ol>
				</nav>

				{/* Sections */}
				<div className="space-y-10 rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
					{sections.map((s, i) => (
						<section
							key={s.id}
							id={s.id}
							className={i !== 0 && s.title ? 'border-t border-slate-100 pt-8' : ''}
						>
							{s.title && (
								<h2
									className="mb-4 text-xl font-bold text-slate-900"
									dangerouslySetInnerHTML={{ __html: s.title }}
								/>
							)}
							{s.content}
						</section>
					))}
				</div>
			</main>
			<Footer />
		</div>
	);
}

export default DataDeletion;
